/**
 * Recommendation Service — Audit Findings v1
 * Recommendations from audit actions type 'recomandare'.
 */

import {
  collection,
  doc,
  addDoc,
  updateDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  runTransaction,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';
import { db } from '../firebase';
import auditService from './auditService';

const COLLECTION = 'recommendations';
const SEQ_COLLECTION = 'org_sequences';

function omitUndefined(obj) {
  if (obj == null || typeof obj !== 'object') return obj;
  const out = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v !== undefined) out[k] = v;
  }
  return out;
}

export class RecommendationService {
  constructor() {
    this.collectionRef = collection(db, COLLECTION);
  }

  async _generateRecommendationCode(organizationId) {
    const seqRef = doc(db, SEQ_COLLECTION, organizationId);
    const seq = await runTransaction(db, async (tx) => {
      const snap = await tx.get(seqRef);
      const current = snap.exists() ? snap.data().rec_last_seq || 0 : 0;
      const next = current + 1;
      if (snap.exists()) {
        tx.update(seqRef, { rec_last_seq: next, updated_at: serverTimestamp() });
      } else {
        tx.set(seqRef, { rec_last_seq: next, updated_at: serverTimestamp() });
      }
      return next;
    });
    const year = new Date().getFullYear();
    return `REC-${year}-${String(seq).padStart(3, '0')}`;
  }

  async createFromAuditAction(auditId, auditData, action, userId) {
    if (!auditId) throw new Error('[RecommendationService] auditId obligatoriu');
    if (!action.id) throw new Error('[RecommendationService] action.id obligatoriu');
    if (!auditData.organizationId) throw new Error('[RecommendationService] organizationId obligatoriu');

    if (action.recommendationId) {
      console.warn('[RecommendationService] action already has recommendationId — skipping:', action.id);
      return action.recommendationId;
    }

    const rec_code = await this._generateRecommendationCode(auditData.organizationId);
    const now = Timestamp.now();

    const auditCorrectiveActions = (auditData.actions || [])
      .filter((a) => ['corectie', 'capa'].includes(a.type))
      .map((a) => ({
        actionId: a.id,
        description: a.description || '',
        type: a.type,
        capaId: a.capaId || null,
        status: a.status || 'open'
      }));

    const payload = omitUndefined({
      recommendation_code: rec_code,
      organizationId: auditData.organizationId,
      auditId,
      auditTitle: auditData.title || '',
      auditType: auditData.auditType || '',
      auditDate: auditData.plannedDate || null,
      auditorName: auditData.auditorName || '',
      auditActionId: action.id,
      auditCorrectiveActions,
      description: action.description || '',
      rationale: action.rationale || '',
      standardRef: action.standardRef || null,
      assignedTo: action.assignedTo || null,
      assignedToName: action.assignedToName || '',
      assignedToGroup: action.assignedToGroup || null,
      assignedToGroupName: action.assignedToGroupName || '',
      dueDate: action.dueDate || null,
      status: 'open',
      taskInstanceId: null,
      implementedAt: null,
      implementedBy: null,
      createdAt: now,
      createdBy: userId,
      updatedAt: now,
      updatedBy: userId
    });

    const ref = await addDoc(this.collectionRef, payload);

    this._generateTask(ref.id, rec_code, payload, userId).catch((e) =>
      console.warn('[RecommendationService] _generateTask failed (non-blocking):', e)
    );

    try {
      auditService.logCreate(COLLECTION, ref.id, rec_code, {
        auditId,
        organizationId: auditData.organizationId
      });
    } catch (e) {
      console.warn('[RecommendationService] audit log failed:', e);
    }

    return ref.id;
  }

  async _generateTask(recommendationId, recCode, recData, userId) {
    if (!recData.assignedTo && !recData.assignedToGroup) {
      console.warn('[RecommendationService] no owner — task not generated for recommendation:', recommendationId);
      return;
    }

    const taskData = omitUndefined({
      name: recData.description,
      type: 'recommendation_action',
      taskType: 'recommendation_action',
      status: 'assigned',
      triggeredBy: 'recommendation',
      source: 'recommendation',
      source_id: recommendationId,
      source_ref: recCode,
      assignedTo: recData.assignedTo || null,
      assignedToName: recData.assignedToName || null,
      assignedToGroup: recData.assignedToGroup || null,
      assignedToGroupName: recData.assignedToGroupName || null,
      scheduledEnd: recData.dueDate || null,
      organizationId: recData.organizationId,
      organization_id: recData.organizationId,
      protocols: [],
      created_at: serverTimestamp(),
      created_by: userId,
      updated_at: serverTimestamp(),
      updated_by: userId
    });

    const taskRef = await addDoc(collection(db, 'task_instances'), taskData);

    const recRef = doc(this.collectionRef, recommendationId);
    await updateDoc(recRef, {
      taskInstanceId: taskRef.id,
      updatedAt: Timestamp.now(),
      updatedBy: userId
    });
  }

  async getRecommendation(recommendationId) {
    const ref = doc(this.collectionRef, recommendationId);
    const snap = await getDoc(ref);
    if (!snap.exists()) return null;
    return { id: snap.id, ...snap.data() };
  }

  async getByOrganization(organizationId, filters = {}) {
    try {
      const q = query(
        this.collectionRef,
        where('organizationId', '==', organizationId),
        orderBy('createdAt', 'desc')
      );
      const snap = await getDocs(q);
      let items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

      if (filters.status) items = items.filter((i) => i.status === filters.status);
      if (filters.auditId) items = items.filter((i) => i.auditId === filters.auditId);
      if (filters.assignedTo) items = items.filter((i) => i.assignedTo === filters.assignedTo);

      return items;
    } catch (e) {
      console.error('[RecommendationService] getByOrganization failed:', e);
      return [];
    }
  }

  async getByAudit(auditId) {
    try {
      const q = query(
        this.collectionRef,
        where('auditId', '==', auditId),
        orderBy('createdAt', 'desc')
      );
      const snap = await getDocs(q);
      return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    } catch (e) {
      console.error('[RecommendationService] getByAudit failed:', e);
      return [];
    }
  }

  async markImplemented(recommendationId, userId) {
    if (!recommendationId) return;
    const ref = doc(this.collectionRef, recommendationId);
    await updateDoc(ref, {
      status: 'implemented',
      implementedAt: Timestamp.now(),
      implementedBy: userId,
      updatedAt: Timestamp.now(),
      updatedBy: userId
    });
    try {
      auditService.logUpdate(COLLECTION, recommendationId, recommendationId, {
        action: 'markImplemented'
      });
    } catch (e) {
      console.warn('[RecommendationService] audit log markImplemented failed:', e);
    }
  }

  async assignOwnerAndGenerateTask(recommendationId, ownerData, userId) {
    const rec = await this.getRecommendation(recommendationId);
    if (!rec) throw new Error('[RecommendationService] recommendation not found');
    if (rec.taskInstanceId) {
      console.warn('[RecommendationService] task already exists — skipping');
      return;
    }

    const ref = doc(this.collectionRef, recommendationId);
    await updateDoc(ref, {
      assignedTo: ownerData.assignedTo || null,
      assignedToName: ownerData.assignedToName || '',
      assignedToGroup: ownerData.assignedToGroup || null,
      assignedToGroupName: ownerData.assignedToGroupName || '',
      updatedAt: Timestamp.now(),
      updatedBy: userId
    });

    const updatedRec = {
      ...rec,
      ...ownerData
    };
    await this._generateTask(recommendationId, rec.recommendation_code, updatedRec, userId);
  }
}

export const recommendationService = new RecommendationService();
